import { Popper, TextField } from "@mui/material";
import type { PopperProps } from "@mui/material/Popper";
import React from "react";
import { ExclamationIcon } from "../../../assets/iconComponents/ExclamationIcon";
import {
  FieldContainer,
  FieldDisplayValue,
  FieldInput,
  FieldTitle,
  CheckboxInputField,
  RadioInputField,
  AutoCompleteTextfieldWrapper,
  AutocompleteOptionReason,
  ChoiceInputRow,
  FormFieldAutocomplete,
  AUTOCOMPLETE_LISTBOX_PROPS,
  AUTOCOMPLETE_POPPER_Z_INDEX,
} from "./FormField.styles";

/** Portal popper with dialog-safe z-index; sizing belongs on the listbox. */
const AutocompletePopper = (props: PopperProps) => (
  <Popper
    {...props}
    style={{ ...props.style, zIndex: AUTOCOMPLETE_POPPER_Z_INDEX }}
  />
);

export type FormFieldAutocompleteOption = {
  id: string;
  label: string;
  disabledReason?: string | null;
};

type UserReportFieldProps = {
  fieldTitle: string;
  fieldValue: string;
  fieldChecked?: boolean;
  placeholderOnError?: string;
  autoCompleteOptions?: string[];
  /** Labels need not be unique; onFieldChange receives `id`. */
  autoCompleteStructuredOptions?: FormFieldAutocompleteOption[];
  autoCompleteDropdownStyle?: boolean;
  autoCompleteTextFieldVariant?: "standard" | "outlined";
  fieldType?:
    | "date"
    | "text"
    | "radio"
    | "result"
    | "checkbox"
    | "autocomplete";
  onFieldChange?: (val: string | boolean) => void;
  edit?: boolean;
  isError?: boolean;
  styles?: Record<string, unknown>;
  hideTitle?: boolean;
  newRequisition?: boolean;
  autoFill?: boolean;
  autoCompletePlaceholder?: string;
  autoCompleteFreeSolo?: boolean;
  autoCompleteNoOptionsText?: string;
  maxCharacters?: number;
  cardDisplay?: boolean;
};

const FormField: React.FC<UserReportFieldProps> = ({
  fieldTitle,
  fieldValue,
  fieldType = "text",
  placeholderOnError,
  autoCompleteOptions = [],
  autoCompleteStructuredOptions,
  autoCompleteDropdownStyle,
  autoCompleteTextFieldVariant = "standard",
  fieldChecked,
  onFieldChange,
  isError = false,
  edit = false,
  styles = {},
  hideTitle,
  newRequisition,
  autoFill = false,
  autoCompletePlaceholder,
  autoCompleteFreeSolo,
  autoCompleteNoOptionsText,
  maxCharacters,
  cardDisplay = false,
}: UserReportFieldProps) => {
  const handleFieldChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (onFieldChange) {
      onFieldChange(e?.target?.value || "");
    }
  };

  const displayInput = (
    type: "date" | "text" | "radio" | "result" | "checkbox" | "autocomplete",
  ) => {
    if (
      cardDisplay &&
      !edit &&
      (type === "text" || type === "date" || type === "autocomplete")
    ) {
      return (
        <FieldDisplayValue data-field-value>
          {fieldValue || (isError ? "" : "-")}
        </FieldDisplayValue>
      );
    }
    switch (type) {
      case "date":
        return (
          <FieldInput
            $cardDisplay={cardDisplay}
            $isError={isError}
            $edit={edit}
            id="new-test-npi-input"
            type={isError && edit ? "date" : "text"}
            value={fieldValue}
            onChange={handleFieldChange}
            onFocus={(e) => {
              e.target.type = "date";
            }}
            onBlur={(e) => {
              e.target.type = "text";
            }}
            placeholder=""
            disabled={!edit}
            tabIndex={edit ? undefined : -1}
            autoComplete={autoFill ? "" : "nope"}
          />
        );
      case "radio":
        return (
          <ChoiceInputRow>
            <RadioInputField>
              <input
                type="radio"
                onClick={() => {
                  if (onFieldChange) {
                    onFieldChange(!fieldChecked);
                  }
                }}
                checked={fieldChecked}
                disabled={!edit}
                tabIndex={edit ? undefined : -1}
                autoComplete={autoFill ? "" : "nope"}
              />
              <label>{fieldValue}</label>
            </RadioInputField>
          </ChoiceInputRow>
        );
      case "checkbox":
        return (
          <ChoiceInputRow>
            <CheckboxInputField>
              <input
                type="checkbox"
                onClick={() => {
                  if (onFieldChange) {
                    onFieldChange(!fieldChecked);
                  }
                }}
                checked={fieldChecked}
                disabled={!edit}
                tabIndex={edit ? undefined : -1}
              />
              <label>{fieldValue}</label>
            </CheckboxInputField>
          </ChoiceInputRow>
        );
      case "autocomplete": {
        const isStructured = Boolean(autoCompleteStructuredOptions?.length);
        const options: FormFieldAutocompleteOption[] = isStructured
          ? (autoCompleteStructuredOptions ?? [])
          : autoCompleteOptions.map((label) => ({ id: label, label }));
        const selected =
          options.find((option) => option.id === fieldValue) ?? null;
        // freeSolo string lists keep the raw field value so typing stays in sync.
        const value =
          isStructured || !autoCompleteFreeSolo ? selected : fieldValue || null;

        const handleAutocompleteChange = (
          _event: React.SyntheticEvent,
          newValue: string | FormFieldAutocompleteOption | null,
        ) => {
          if (!onFieldChange) {
            return;
          }
          if (typeof newValue === "string") {
            onFieldChange(newValue);
            return;
          }
          onFieldChange(newValue?.id ?? "");
        };

        const handleAutocompleteInputChange = (
          _event: React.SyntheticEvent,
          newInputValue: string,
          reason: string,
        ) => {
          // Skip MUI "reset" — syncing it into Recoil can hit max update depth.
          if (!onFieldChange || reason === "reset" || isStructured) {
            return;
          }
          if (!autoCompleteFreeSolo && reason !== "clear") {
            return;
          }
          onFieldChange(newInputValue);
        };

        return (
          <FormFieldAutocomplete
            freeSolo={autoCompleteFreeSolo}
            ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
            PopperComponent={AutocompletePopper}
            aria-autocomplete="none"
            disabled={!edit}
            value={value}
            onChange={handleAutocompleteChange}
            onInputChange={handleAutocompleteInputChange}
            options={options}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            isOptionEqualToValue={(option, optionValue) =>
              option.id ===
              (typeof optionValue === "string" ? optionValue : optionValue.id)
            }
            getOptionDisabled={(option) => Boolean(option.disabledReason)}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.label}
                {option.disabledReason && (
                  <AutocompleteOptionReason>
                    {option.disabledReason}
                  </AutocompleteOptionReason>
                )}
              </li>
            )}
            noOptionsText={autoCompleteNoOptionsText}
            placeholder={isError && edit ? placeholderOnError : ""}
            renderInput={(params) =>
              autoCompleteDropdownStyle ? (
                <AutoCompleteTextfieldWrapper>
                  <TextField
                    {...params}
                    label={
                      autoCompleteTextFieldVariant === "outlined"
                        ? fieldTitle
                        : undefined
                    }
                    variant={autoCompleteTextFieldVariant}
                    inputProps={{
                      ...params.inputProps,
                      maxLength: 255,
                      "aria-autocomplete": "none",
                      autoComplete: "nope",
                      placeholder: autoCompletePlaceholder,
                    }}
                    autoComplete="nope"
                  />
                </AutoCompleteTextfieldWrapper>
              ) : (
                <div ref={params.InputProps.ref}>
                  <FieldInput
                    $cardDisplay={cardDisplay}
                    $isError={isError}
                    $edit={edit}
                    {...params.inputProps}
                    autoComplete="nope"
                    placeholder={autoCompletePlaceholder}
                    maxLength={255}
                  />
                </div>
              )
            }
          />
        );
      }
      default:
        return (
          <FieldInput
            $cardDisplay={cardDisplay}
            $isError={isError}
            $edit={edit}
            type={fieldType}
            value={fieldValue || (isError || edit ? "" : "-")}
            onChange={handleFieldChange}
            placeholder={isError && edit ? placeholderOnError : ""}
            tabIndex={edit ? undefined : -1}
            disabled={!edit}
            autoComplete={autoFill ? "" : "nope"}
            maxLength={maxCharacters}
          />
        );
    }
  };

  return (
    <FieldContainer
      style={styles}
      edit={edit}
      $isError={isError}
      $cardDisplay={cardDisplay}
    >
      {!hideTitle && (
        <FieldTitle
          $isError={isError}
          $cardDisplay={cardDisplay}
          newRequisition={newRequisition}
        >
          {fieldTitle} {isError && <ExclamationIcon />}
        </FieldTitle>
      )}
      {displayInput(fieldType)}
    </FieldContainer>
  );
};

export default FormField;
